import { useEffect, useRef, useState, type FormEvent } from "react";
import { authFehler, authFehlerArt } from "../konto/auth";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useSession, useSwitchStaffOrg, useSwitchTalentOrg } from "../../app/session";
import { WEBSITE_URL } from "../../app/nav";
import { api } from "../../data";
import { PublicLayout } from "../../components/shell/WebLayout";
import { DarstellungAbschnitt, saveThemeChoice, useDarstellungEntwurf } from "../../components/Darstellung";
import { acceptInvitation, adoptInvitationLanguage, einladungsSprache, invitationEmailMatches, invitationPreview, recordConsent, recordGuardianConsent, type InvitationPreview } from "./actions";
import { dateOf, tr, type Lang } from "./lang";
import { describeCreatorError } from "./fehler";
import { MIN_PASSWORD_LENGTH, passwordHint, passwordLengthProblem } from "@talentkontor/domain";
import { CaptchaFeld, captchaConfig } from "../konto/Captcha";
import { PostfachPruefen } from "../konto/PostfachPruefen";
import { GoogleAnmeldung } from "../../components/GoogleAnmeldung";
import { forgetPendingConsent, oauthConsentFor, rememberOAuthConsent } from "../../lib/zustimmung-vormerkung";

/**
 * Einladung annehmen (12_app_creator/einladung.html), öffentlich erreichbar über /einladung/:token und den Mail-Link
 * /einladung?t=… (0025). Beim Öffnen zeigt die öffentliche Vorschau (invitation_public_view, 0031) Agentur, Art der
 * Einladung, Gültigkeit und einen Adresshinweis; ungültige Links fallen sofort auf, nicht erst nach dem Anlegen des
 * Kontos. Die Zustimmung zu Nutzungsbedingungen und Datenschutz wird mit Stand gespeichert (consent_record).
 * Erster Schritt für Creator und Teammitglieder: hier wählen sie auch die Darstellung (0121, Dunkel vorausgewählt, wirkt
 * sofort); mit „Einladung annehmen“ bzw. „Konto anlegen“ kommt sie ins Konto (theme und theme_chosen_at), wie die Sprache.
 */


export function Einladung() {
  const { token: pathToken } = useParams();
  const [params] = useSearchParams();
  const token = (pathToken ?? params.get("t") ?? "").trim();
  const { session, ready } = useSession();
  const switchTalent = useSwitchTalentOrg();
  const switchStaff = useSwitchStaffOrg();
  const navigate = useNavigate();
  // Deutsch, auf Wunsch Englisch (Link ?sprache=en oder Umschalter)
  const [lang, setLang] = useState<Lang>(params.get("sprache") === "en" ? "en" : "de");
  // Ausdrücklich gewählt (Link mit ?sprache= oder Umschalter): nur dann wird sie Vorgabe fürs Portal (CREATOR-R2-9)
  const [languageChosen, setLanguageChosen] = useState(params.has("sprache"));
  const languageChosenRef = useRef(languageChosen);
  languageChosenRef.current = languageChosen;
  const t = (de: string, en: string) => tr(lang, de, en);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeFuer, setCodeFuer] = useState<string | null>(null);
  const [mode, setMode] = useState<"neu" | "anmelden">("neu");
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  // Minderjährige (Praxisfall): Name, Adresse und Erklärung der erziehungsberechtigten Person
  const [guardian, setGuardian] = useState({ name: "", email: "", confirmed: false });
  // Darstellung (0121): Teil dieses ersten Schritts, gespeichert mit der Annahme
  const [darstellung, setDarstellung] = useDarstellungEntwurf(session?.userId ?? null);
  // Probelauf N3: Passt das angemeldete Konto zur Einladung? false = andere Adresse (kein „Annehmen“), null = unbekannt
  const [kontoPasst, setKontoPasst] = useState<boolean | null>(null);
  // Supabase Auth verlangt bei eingeschaltetem CAPTCHA auch beim Anlegen über die Einladung ein Token (Befund 02.10.2026)
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  // Zustimmung im Formular „Konto anlegen“ (auch Voraussetzung für „Mit Google fortfahren“)
  const [zugestimmt, setZugestimmt] = useState(false);
  // Bestätigungslink schon benutzt oder abgelaufen: Supabase hängt #error=…&error_code=otp_expired an (Befund 02.10.2026)
  const [linkFehler] = useState(() => typeof window !== "undefined" && /(^|[#&])error(_code)?=/.test(window.location.hash));
  const sessionEmail = session?.email ?? null;
  useEffect(() => {
    let alive = true;
    setKontoPasst(null);
    if (!token || !sessionEmail) return;
    invitationEmailMatches(api, token, sessionEmail).then((m) => alive && setKontoPasst(m)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [token, sessionEmail]);
  // Zurück zur Einladung in derselben Sprache (SICHT-OEFFENTLICH-BETRIEB-05)
  const weiter = `/einladung/${encodeURIComponent(token)}${lang === "en" ? "?sprache=en" : ""}`;

  useEffect(() => {
    let alive = true;
    if (!token) return;
    invitationPreview(api, token).then((p) => {
      if (!alive) return;
      setPreview(p);
      // Link ohne Sprache (z. B. aus einer älteren Mail): Sprache der Einladungsmail, solange niemand umgeschaltet hat
      if (!params.has("sprache")) setLang((vorher) => (languageChosenRef.current ? vorher : einladungsSprache(null, p)));
    }).catch(() => alive && setPreview({ state: "unknown" }));
    return () => {
      alive = false;
    };
  }, [token]);

  const agency = preview?.agency?.trim() || null;
  const team = preview?.kind === "member";
  const invalid = preview && preview.state !== "open";
  const minor = !!preview?.guardian_required;
  const guardianProblem = (): string | null => {
    if (!minor) return null;
    if (guardian.name.trim().length < 2) return t("Bitte den Namen deiner erziehungsberechtigten Person angeben.", "Please enter the name of your parent or guardian.");
    // CREATOR-2: die Adresse ist Pflicht; die Eltern bekommen dorthin einen Hinweis und später die Verträge
    if (!guardian.email.trim()) return t("Bitte die E-Mail-Adresse deiner erziehungsberechtigten Person angeben.", "Please enter the email address of your parent or guardian.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guardian.email.trim())) return t("Die E-Mail-Adresse der erziehungsberechtigten Person ist ungültig.", "The email address of your parent or guardian is invalid.");
    if (!guardian.confirmed) return t("Deine erziehungsberechtigte Person muss die Erklärung bestätigen.", "Your parent or guardian must confirm the statement.");
    return null;
  };
  const failText = (e: unknown) => {
    // Praxistest K-221: Meldungen von Supabase Auth (Konto existiert, Passwort geleakt, Sicherheitsprüfung, Limit) im Klartext
    if (authFehlerArt(e) !== "unknown") return authFehler(e, lang);
    const d = describeCreatorError(e, lang);
    return `${d.title === "Das hat nicht geklappt." || d.title === "That did not work." ? "" : `${d.title} `}${d.text}`.trim();
  };

  async function accept(withConsent: boolean) {
    const gp = guardianProblem();
    if (gp) {
      setError(gp);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (minor) await recordGuardianConsent(api, token, guardian);
      const r = await acceptInvitation(api, token);
      // Zustimmung mit Stand speichern (nur, wer sie beim Anlegen gegeben hat)
      // Nach der Bestätigungsmail kommt man angemeldet zurück: die Zustimmung vom Anlegen steht dann im Konto
      const ich = api.session();
      const consent = withConsent || (await api.signupConsentGiven?.().catch(() => false)) === true
        || (!!ich && oauthConsentFor({ userId: ich.userId, email: ich.email ?? "" }));
      if (consent) await recordConsent(api).then(() => forgetPendingConsent()).catch(() => undefined);
      // CREATOR-R2-9: die gewählte Sprache der Einladung als Vorgabe übernehmen, bevor das Konto neu lädt
      const me = api.session()?.userId;
      if (me && languageChosen) await adoptInvitationLanguage(api, me, lang).catch(() => undefined);
      // Darstellung aus diesem Schritt ins Konto (theme, theme_chosen_at); scheitert das, gilt sie auf diesem Gerät
      if (me) await saveThemeChoice(me, darstellung);
      // Die neue Agentur gleich auswählen; das Konto (Verknüpfungen) lädt dabei neu
      if (r.kind === "member") switchStaff(r.org_id);
      else switchTalent(r.org_id);
      navigate(r.kind === "member" ? "/agentur" : "/creator", { replace: true });
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const text = failText(e);
      setError(/andere E-Mail/.test(raw) ? `${text} ${t("Melde dich mit der Adresse an, an die die Einladung ging.", "Sign in with the address the invitation was sent to.")}` : /ungültig|abgelaufen/.test(raw) ? `${text} ${t("Bitte fordere einen neuen Link bei deiner Agentur an.", "Please ask your agency for a new link.")}` : text);
    } finally {
      setBusy(false);
    }
  }

  async function signUp(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    const pwProblem = api.mode !== "demo" ? passwordLengthProblem(password, lang) : null;
    if (pwProblem) {
      setError(pwProblem);
      return;
    }
    if (mitCaptcha && !captcha) {
      setError(t("Bitte die Sicherheitsprüfung lösen.", "Please complete the security check."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Befund 33: die Adresse vor dem Anlegen des Kontos gegen die Einladung prüfen (die Antwort verrät die Adresse nicht)
      const matches = await invitationEmailMatches(api, token, email).catch(() => null);
      if (matches === false) {
        setError(t(`Diese Adresse passt nicht zur Einladung${preview?.email_hint ? ` (sie ging an ${preview.email_hint})` : ""}. Nutze die Adresse aus der Einladungs-Mail; sonst bitte deine Agentur um eine neue Einladung.`,
          `This address does not match the invitation${preview?.email_hint ? ` (it was sent to ${preview.email_hint})` : ""}. Use the address from the invitation email; otherwise ask your agency for a new invitation.`));
        return;
      }
      // Minderjährige: Einwilligung vor dem Anlegen des Kontos vollständig
      const gp = guardianProblem();
      if (gp) {
        setError(gp);
        return;
      }
      // Der Bestätigungslink führt direkt zurück zu dieser Einladung
      const r = await api.signUp(email, password, { redirectTo: weiter, captchaToken: captcha, termsConsent: true, language: lang });
      if (r.needsConfirmation) {
        // Inhaber 06.10.2026: Code aus der Mail auf dieser Seite eintippen; danach ist man angemeldet und nimmt hier an
        setCodeFuer(email.trim());
        return;
      }
      await accept(true);
    } catch (err) {
      setError(failText(err));
    } finally {
      setBusy(false);
      // Tokens gelten einmal: nach jedem Versuch neu lösen
      if (mitCaptcha) setCaptchaRunde((n) => n + 1);
    }
  }

  const stateText: Record<InvitationPreview["state"], string> = {
    open: "",
    expired: t("Diese Einladung ist abgelaufen.", "This invitation has expired."),
    revoked: t("Diese Einladung wurde zurückgezogen.", "This invitation has been withdrawn."),
    accepted: t("Diese Einladung wurde schon angenommen.", "This invitation has already been accepted."),
    unknown: t("Diesen Einladungslink gibt es nicht.", "This invitation link does not exist."),
  };

  return (
    // Absender ist die einladende Agentur (Kopf und Fuß), wie auf Freigabe, Bericht und Signierseite (SICHT-OEFFENTLICH-BETRIEB-28)
    <PublicLayout lang={lang} sender={agency ? { name: agency } : null}>
      {/* Eigener Tab-Titel statt nur „CreatorOffice“ (SICHT-OEFFENTLICH-BETRIEB-33) */}
      <title>{`${t("Einladung", "Invitation")} · ${agency ?? "CreatorOffice"}`}</title>
      {/* Schmale Spalte wie auf /registrieren, mit Abstand zwischen Kopf, Karte und Hinweis (SICHT-OEFFENTLICH-BETRIEB-31) */}
      <main id="main" className="auth-shell stack tk-einladung">
        <div className="pagehead">
          <div>
            <div className="eyebrow">{team ? t("Dein Zugang fürs Team", "Your team access") : t("Dein Creator-Portal", "Your creator portal")}</div>
            <h1>{agency ? t(`Willkommen bei ${agency}.`, `Welcome to ${agency}.`) : t("Willkommen bei CreatorOffice.", "Welcome to CreatorOffice.")}</h1>
            <p>
              {preview?.state === "open" && preview.email_hint ? t(`Die Einladung ging an ${preview.email_hint}.`, `The invitation was sent to ${preview.email_hint}.`) : ""}
              {preview?.state === "open" && preview.expires_at ? t(` Gültig bis ${dateOf(preview.expires_at, lang)}.`, ` Valid until ${dateOf(preview.expires_at, lang)}.`) : ""}
            </p>
          </div>
          <button type="button" onClick={() => { setLang(lang === "en" ? "de" : "en"); setLanguageChosen(true); }} lang={lang === "en" ? "de" : "en"}>{lang === "en" ? "Deutsch" : "English"}</button>
        </div>
        {linkFehler && (
          <div className="notice" role="status">
            {session
              ? t("Der Bestätigungslink aus der Mail war schon benutzt oder ist abgelaufen. Du bist trotzdem angemeldet; mach hier einfach weiter.",
                "The confirmation link from the email was already used or has expired. You are signed in anyway; just continue here.")
              : t("Der Bestätigungslink aus der Mail war schon benutzt oder ist abgelaufen. Melde dich mit deiner Adresse und deinem Passwort an; danach kommst du hierher zurück.",
                "The confirmation link from the email was already used or has expired. Sign in with your address and password; afterwards you come back here.")}
          </div>
        )}
        {!token ? (
          <div className="panel stack" role="alert">
            <h2>{t("Dieser Einladungslink ist unvollständig.", "This invitation link is incomplete.")}</h2>
            <p>{t("Öffne den Link aus deiner Einladungs-Mail vollständig oder bitte deine Agentur um einen neuen Link.", "Open the link from your invitation email completely or ask your agency for a new link.")}</p>
          </div>
        ) : invalid ? (
          <div className="panel stack" role="alert">
            <h2>{stateText[preview!.state]}</h2>
            <p>{preview!.state === "accepted"
              ? t("Melde dich mit deinem Konto an; die Agentur ist dort schon verbunden.", "Sign in with your account; the agency is already connected there.")
              : t(`Bitte ${agency ? agency : "deine Agentur"} um einen neuen Einladungslink.`, `Please ask ${agency ?? "your agency"} for a new invitation link.`)}</p>
            <Link className="btn primary" to="/anmelden">{t("Zur Anmeldung", "Go to sign-in")}</Link>
          </div>
        ) : !ready ? (
          <p className="muted">{t("Wird geladen …", "Loading …")}</p>
        ) : session && kontoPasst === false ? (
          <div className="panel stack" role="alert">
            <h2>{t("Diese Einladung gilt für eine andere E-Mail-Adresse.", "This invitation is for a different email address.")}</h2>
            <p>{t(`Du bist angemeldet als ${session.email}${preview?.email_hint ? `; die Einladung ging an ${preview.email_hint}` : ""}. Annehmen kann sie nur das eingeladene Konto. Melde dich ab und mit der Adresse aus der Einladungs-Mail an.`,
              `You are signed in as ${session.email}${preview?.email_hint ? `; the invitation was sent to ${preview.email_hint}` : ""}. Only the invited account can accept it. Sign out and sign in with the address from the invitation email.`)}</p>
            <div className="row">
              <button className="primary" onClick={() => void api.signOut()}>{t("Abmelden und mit anderem Konto anmelden", "Sign out and sign in with another account")}</button>
            </div>
          </div>
        ) : session ? (
          <div className="panel stack">
            <h2>{agency ? t(`${agency} hat dich eingeladen.`, `${agency} has invited you.`) : t("Deine Agentur hat dich eingeladen.", "Your agency has invited you.")}</h2>
            <p>{t(`Du bist angemeldet als ${session.email}. Mit „Einladung annehmen“ verbindest du dieses Konto mit ${agency ?? "deiner Agentur"}.`, `You are signed in as ${session.email}. “Accept invitation” connects this account with ${agency ?? "your agency"}.`)}</p>
            <DarstellungAbschnitt value={darstellung} onChange={setDarstellung} t={t} liste />
            {minor && <GuardianFields lang={lang} agency={agency} value={guardian} onChange={setGuardian} />}
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button className="primary" onClick={() => void accept(false)} disabled={busy}>{busy ? t("Wird angenommen …", "Accepting …") : t("Einladung annehmen", "Accept invitation")}</button>
              <button onClick={() => void api.signOut()} disabled={busy}>{t("Mit anderem Konto anmelden", "Sign in with another account")}</button>
            </div>
          </div>
        ) : codeFuer ? (
          <PostfachPruefen email={codeFuer} redirectTo={weiter} ziel="einladung" onBack={() => setCodeFuer(null)} onVerified={() => setCodeFuer(null)} />
        ) : mode === "neu" ? (
          <form className="panel stack" onSubmit={(e) => void signUp(e)} aria-busy={busy}>
            <h2>{agency ? t(`${agency} hat dich eingeladen.`, `${agency} has invited you.`) : t("Deine Agentur hat dich eingeladen.", "Your agency has invited you.")}</h2>
            <p>{t("Lege dein Konto mit der E-Mail-Adresse an, an die die Einladung ging.", "Create your account with the email address the invitation was sent to.")}</p>
            <GoogleAnmeldung t={t} redirectPath={weiter} vorher={() => {
              if (!zugestimmt) return t("Bitte zuerst unten die Nutzungsbedingungen akzeptieren.", "Please first accept the terms of use below.");
              if (minor) return t("Bei Minderjährigen bitte das Formular unten nutzen; die Einwilligung der Eltern gehört dazu.", "For minors please use the form below; it includes the parents' consent.");
              rememberOAuthConsent(["user_terms", "privacy"]);
              return null;
            }} fenster={{
              // Inhaber 13.10.2026: Zustimmung im Fenster nach dem Klick auf Google (Minderjährige bleiben beim Formular)
              offen: !zugestimmt && !minor, bereit: zugestimmt, titel: t("Kurz bestätigen", "Quick confirmation"),
              inhalt: <>
                <p>{t("Bitte bestätige noch die Bedingungen für dein Konto. Danach geht es mit Google direkt weiter.", "Please confirm the terms for your account. After that you continue with Google right away.")}</p>
                <label className="row tk-check">
                  <input type="checkbox" checked={zugestimmt} onChange={(e) => setZugestimmt(e.target.checked)} />
                  <span>
                    {t("Ich akzeptiere die ", "I accept the ")}<a href={`${WEBSITE_URL}/nutzung.html`} target="_blank" rel="noopener noreferrer">{t("Nutzungsbedingungen", "terms of use")}</a>{t(" und habe die ", " and have read the ")}<a href={`${WEBSITE_URL}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{t("Datenschutzhinweise", "privacy notice")}</a>{t(" gelesen.", ".")}
                  </span>
                </label>
              </>,
            }} />
            <label>
              {t("E-Mail", "Email")}
              <input name="email" type="email" required autoComplete="email" aria-describedby="einladung-adresse" />
              {preview?.email_hint && <small id="einladung-adresse">{t(`Die Einladung ging an ${preview.email_hint}.`, `The invitation was sent to ${preview.email_hint}.`)}</small>}
            </label>
            <label>
              {t("Passwort", "Password")}
              <input name="password" type="password" minLength={api.mode === "demo" ? undefined : MIN_PASSWORD_LENGTH} required={api.mode !== "demo"} autoComplete="new-password" aria-describedby="einladung-pw" />
              {api.mode !== "demo" && <small id="einladung-pw">{passwordHint(lang)}</small>}
            </label>
            <DarstellungAbschnitt value={darstellung} onChange={setDarstellung} t={t} liste />
            <label className="row tk-check">
              <input type="checkbox" required checked={zugestimmt} onChange={(e) => setZugestimmt(e.target.checked)} />
              <span>
                {t("Ich akzeptiere die ", "I accept the ")}<a href={`${WEBSITE_URL}/nutzung.html`} target="_blank" rel="noopener noreferrer">{t("Nutzungsbedingungen", "terms of use")}</a>{t(" und habe die ", " and have read the ")}<a href={`${WEBSITE_URL}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{t("Datenschutzhinweise", "privacy notice")}</a>{t(" gelesen.", ".")}
              </span>
            </label>
            {minor && <GuardianFields lang={lang} agency={agency} value={guardian} onChange={setGuardian} />}
            {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
            {error && <p className="tk-error" role="alert">{error}</p>}
            <button className="primary" type="submit" disabled={busy}>{busy ? t("Wird angelegt …", "Creating …") : t("Konto anlegen", "Create account")}</button>
            <button type="button" onClick={() => { setMode("anmelden"); setError(null); }}>{t("Ich habe schon ein Konto", "I already have an account")}</button>
            {api.mode === "demo" && <small>{t("Demo: Das Konto entsteht nur in diesem Browser, ohne Passwort und ohne Mail.", "Demo: the account only exists in this browser, without password and without email.")}</small>}
          </form>
        ) : (
          <div className="panel stack">
            <h2>{t("Mit bestehendem Konto annehmen", "Accept with an existing account")}</h2>
            <p>{t("Melde dich an; danach kommst du hierher zurück und nimmst die Einladung an.", "Sign in; afterwards you come back here and accept the invitation.")}</p>
            <Link className="btn primary" to={`/anmelden?weiter=${encodeURIComponent(weiter)}${lang === "en" ? "&sprache=en" : ""}`}>{t("Anmelden", "Sign in")}</Link>
            <button type="button" onClick={() => setMode("neu")}>{t("Neues Konto anlegen", "Create a new account")}</button>
          </div>
        )}
        {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p>}
      </main>
    </PublicLayout>
  );
}

/**
 * Minderjährige Creator: laut Geburtsdatum im Profil unter 18. Die erziehungsberechtigte Person gibt die Erklärung selbst
 * ab (Name, Adresse, Bestätigung); gespeichert mit Stand und Zeitpunkt, die Agentur sieht sie am Profil (0058).
 */
function GuardianFields({ lang, agency, value, onChange }: { lang: Lang; agency: string | null; value: { name: string; email: string; confirmed: boolean }; onChange: (v: { name: string; email: string; confirmed: boolean }) => void }) {
  const t = (de: string, en: string) => tr(lang, de, en);
  const who = agency ?? t("die Agentur", "the agency");
  return (
    <fieldset className="stack tk-gap-sm tk-guardian">
      <legend><strong>{t("Einwilligung deiner Erziehungsberechtigten", "Consent of your parent or guardian")}</strong></legend>
      <p>{t("Laut deinem Profil bist du unter 18. Dieser Teil ist für deine Mutter, deinen Vater oder eine andere erziehungsberechtigte Person: bitte gemeinsam ausfüllen.",
        "According to your profile you are under 18. This part is for your mother, father or another parent or guardian: please fill it in together.")}</p>
      <label>{t("Name der erziehungsberechtigten Person", "Name of parent or guardian")}<input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} autoComplete="off" /></label>
      <label>{t("Ihre E-Mail-Adresse", "Their email address")}<input type="email" required value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })} autoComplete="off" aria-describedby="guardian-mail-hinweis" />
        <small id="guardian-mail-hinweis">{t(`Dorthin schicken wir einen Hinweis auf diese Einwilligung; Verträge schickt ${who} dorthin zur Unterschrift.`, `We send a notice of this consent to this address; ${who} sends contracts there for signature.`)}</small></label>
      <label className="row tk-check">
        <input type="checkbox" checked={value.confirmed} onChange={(e) => onChange({ ...value, confirmed: e.target.checked })} />
        <span>{t(`Ich bin erziehungsberechtigt und bin einverstanden, dass mein Kind ein Konto anlegt, mit ${who} zusammenarbeitet und die Nutzungsbedingungen und Datenschutzhinweise gelten. Verträge unterschreibe ich über meinen eigenen Link.`,
          `I am the parent or guardian and agree that my child creates an account, works with ${who} and that the terms of use and privacy notice apply. I sign contracts via my own link.`)}</span>
      </label>
    </fieldset>
  );
}
